import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import clsx from 'clsx'
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react'

export type ToastVariant ='success' |'error' |'warning' |'info'

export interface Toast {
 id: string
 variant: ToastVariant
 title: string
 message?: string
 duration?: number // ms, 0 = no auto-dismiss
}

interface ToastContextType {
 toasts: Toast[]
 addToast: (toast: Omit<Toast,'id'>) => string
 removeToast: (id: string) => void
 clearToasts: () => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = (): ToastContextType => {
 const ctx = useContext(ToastContext)
 if (!ctx) throw new Error('useToast must be used within ToastProvider')
 return ctx
}

/* ─── Icons ────────────────────────────────────────────────────── */

const iconMap: Record<ToastVariant, React.FC<{ size?: number }>> = {
 success: CheckCircle2,
 error: AlertCircle,
 warning: AlertTriangle,
 info: Info,
}

const variantClasses: Record<ToastVariant, string> = {
  success: 'border-l-4 border-l-teacher-400 bg-white/90',
  error: 'border-l-4 border-l-red-400 bg-white/90',
  warning: 'border-l-4 border-l-amber-400 bg-white/90',
  info: 'border-l-4 border-l-brand-400 bg-white/90',
}

const iconColorClasses: Record<ToastVariant, string> = {
  success: 'text-teacher-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-brand-500',
}

/* ─── Individual Toast Item ─────────────────────────────────────── */

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
 const Icon = iconMap[toast.variant]
 const [isExiting, setIsExiting] = useState(false)

 const handleDismiss = useCallback(() => {
 setIsExiting(true)
 setTimeout(() => onDismiss(toast.id), 200)
 }, [toast.id, onDismiss])

 // Auto-dismiss
 useEffect(() => {
 if (toast.duration === 0) return
 const timer = setTimeout(handleDismiss, toast.duration ?? 4000)
 return () => clearTimeout(timer)
 }, [toast.duration, handleDismiss])

 return (
 <div
 className={clsx(
   'flex items-start gap-3 p-3.5 rounded-xl border shadow-card backdrop-blur-md transition-all',
 variantClasses[toast.variant],
 isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0',
 )}
 >
 <Icon size={18} className={clsx('flex-shrink-0 mt-0.5', iconColorClasses[toast.variant])} />
 <div className="flex-1 min-w-0">
 <p className="text-sm font-semibold text-gray-900">{toast.title}</p>
 {toast.message && (
 <p className="text-xs text-gray-600 mt-0.5">{toast.message}</p>
 )}
 </div>
 <button
 onClick={handleDismiss}
 className="flex-shrink-0 text-gray-500 hover:text-gray-600  transition-colors"
 >
 <X size={16} />
 </button>
 </div>
 )
}

/* ─── Provider ─────────────────────────────────────────────────── */

let toastCounter = 0

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
 const [toasts, setToasts] = useState<Toast[]>([])

 const addToast = useCallback((toast: Omit<Toast,'id'>): string => {
 const id =`toast-${++toastCounter}`
 setToasts((prev) => [...prev, { ...toast, id }])
 return id
 }, [])

 const removeToast = useCallback((id: string) => {
 setToasts((prev) => prev.filter((t) => t.id !== id))
 }, [])

 const clearToasts = useCallback(() => {
 setToasts([])
 }, [])

 return (
 <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
 {children}
 {/* Toast stack — fixed bottom-right */}
 <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
 {toasts.map((toast) => (
 <div key={toast.id} className="pointer-events-auto">
 <ToastItem toast={toast} onDismiss={removeToast} />
 </div>
 ))}
 </div>
 </ToastContext.Provider>
 )
}

/* ─── Convenience hook for common patterns ─────────────────────── */

export function useToastActions() {
  const { addToast } = useToast()
  return React.useMemo(() => ({
    success: (title: string, message?: string) => addToast({ variant: 'success', title, message }),
    error: (title: string, message?: string) => addToast({ variant: 'error', title, message }),
    warning: (title: string, message?: string) => addToast({ variant: 'warning', title, message }),
    info: (title: string, message?: string) => addToast({ variant: 'info', title, message }),
  }), [addToast])
}